import { useEffect, useState, useCallback } from "react";
import { ShareExpiry } from "@/components/ShareExpiry";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { daDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, Loader2, CheckCircle2, MinusCircle, Link2, Share2, RotateCcw } from "lucide-react";
import { FindingCard } from "@/components/le/ObjectCards";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CAT_BADGE = { Afvigelse: "bg-[#F1DEDA] text-[#A83232] border-[#E9C9C3]", Observation: "bg-[#F4EFEC] text-[#806B61] border-[#E6DDD6]", Forslag: "bg-[#E7EADF] text-[#5f6b4a] border-[#D3D8C6]" };
const ST_BADGE = { Åben: "bg-[#F1DEDA] text-[#A83232]", "Afventer kontrol": "bg-[#F4EFEC] text-[#806B61]", Lukket: "bg-[#E7EADF] text-[#5f6b4a]" };
const EV_LABEL = { note: "Notat", status: "Status ændret", responsible: "Ansvarlig ændret", due: "Frist ændret", close: "Lukket", reopen: "Genåbnet", image: "Billede tilføjet" };

function Card({ children, className = "" }) { return <div className={`bg-white rounded-2xl border border-[#EBE8E3] ${className}`}>{children}</div>; }
function Row({ label, value }) { return <div className="text-sm"><span className="text-stone-500">{label}</span><p className="font-medium text-[#1A1919] whitespace-pre-wrap">{value || "—"}</p></div>; }

function ShareDialog({ id, token, setToken, onClose, a, setA }) {
  const url = token ? `${window.location.origin}/audit-rapport/${token}` : "";
  const create = async () => { try { const r = await api.post(`/audit/audits/${id}/share`); setToken(r.data.share_token); toast.success("Delingslink oprettet"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const regen = async () => { try { const r = await api.post(`/audit/audits/${id}/share/regenerate`); setToken(r.data.share_token); toast.success("Nyt link genereret"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async () => { try { await api.delete(`/audit/audits/${id}/share`); setToken(null); toast.success("Deling deaktiveret"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-lg" data-testid="audit-share-dialog">
        <DialogHeader><DialogTitle>Del auditrapport</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-stone-500">Delingssiden viser rapporten uden login. Kun personer med linket kan se den.</p>
          {token ? (
            <>
              <div><p className="text-xs font-medium text-stone-500 mb-1.5">Offentligt link</p>
                <div className="flex gap-2"><Input readOnly value={url} className="rounded-xl text-xs" data-testid="audit-share-url" /><Button variant="outline" onClick={() => { navigator.clipboard.writeText(url); toast.success("Kopieret"); }} className="rounded-xl border-[#EBE8E3]">Kopiér</Button></div>
              </div>
              <ShareExpiry kind="audit" docId={id} expiresAt={a?.share_expires_at} onChange={(v) => setA({ ...a, share_expires_at: v })} />
              <div className="flex gap-3">
                <a href={url} target="_blank" rel="noreferrer" className="text-sm text-[#806B61] hover:underline flex items-center gap-1"><Share2 className="h-3.5 w-3.5" /> Forhåndsvis</a>
                <button onClick={regen} className="text-sm text-stone-500 hover:underline ml-auto">Nyt link</button>
                <button onClick={del} className="text-sm text-[#A83232] hover:underline" data-testid="audit-share-disable">Deaktivér</button>
              </div>
            </>
          ) : (
            <Button onClick={create} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a] w-full" data-testid="audit-share-create"><Share2 className="h-4 w-4 mr-1.5" /> Opret delingslink</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AuditReport() {
  const { id } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const [a, setA] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [token, setToken] = useState(null);
  const [cards, setCards] = useState({});
  const loadCards = useCallback(() => api.get("/le/findings/cards", { params: { audit_id: id } }).then((r) => setCards(Object.fromEntries(r.data.map((c) => [c.id, c])))).catch(() => {}), [id]);
  useEffect(() => { loadCards(); }, [loadCards]);

  useEffect(() => { api.get(`/audit/audits/${id}`).then((r) => { setA(r.data); setToken(r.data.share_token || null); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, [id]);
  useEffect(() => { if (a?.status === "Afsluttet" && loc.state?.openShare) setShareOpen(true); }, [a]); // eslint-disable-line
  const reopen = async () => {
    if (!window.confirm("Genåbn denne audit? Rapporten låses op og status sættes til 'I gang'.")) return;
    try { await api.post(`/audit/audits/${id}/reopen`); toast.success("Audit genåbnet"); nav(`/kvalitet/audit/${id}/udfoer`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!a) return <div className="flex items-center gap-2 text-stone-400"><Loader2 className="h-4 w-4 animate-spin" /> Indlæser…</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="audit-report-page">
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => nav(-1)} className="text-sm text-stone-500 hover:text-stone-900 flex items-center gap-1" data-testid="report-back"><ChevronLeft className="h-4 w-4" /> Tilbage</button>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="report-share" onClick={() => setShareOpen(true)} className="rounded-xl border-[#EBE8E3]"><Share2 className="h-4 w-4 mr-1.5" /> Del rapport</Button>
          {a.status === "Afsluttet" && <Button data-testid="report-reopen" onClick={reopen} className="rounded-xl bg-[#806B61] hover:bg-[#6d5a51]"><RotateCcw className="h-4 w-4 mr-1.5" /> Genåbn audit</Button>}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono text-stone-400">{a.audit_no}</span>
          <span className="text-[11px] px-2 py-0.5 rounded-full border bg-[#E7EADF] text-[#5f6b4a] border-[#D3D8C6]">{a.status}</span>
          {token && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F1DEDA] text-[#A83232] flex items-center gap-1"><Share2 className="h-3 w-3" /> Delt</span>}
        </div>
        <h1 className="text-3xl font-semibold text-[#1A1919] mt-1">{a.title}</h1>
        <p className="text-sm text-stone-500 mt-1">Afsluttet af {a.completed_by_name || "—"} · {a.completed_at ? daDate(a.completed_at) : "—"}</p>
      </div>

      <Card className="p-5 grid sm:grid-cols-2 gap-4">
        <Row label="Planlagt dato" value={daDate(a.planned_date)} />
        <Row label="Faktisk auditdato" value={daDate(a.actual_date)} />
        <Row label="Auditor" value={a.auditor_name} />
        <Row label="Deltagere" value={a.participants} />
        <Row label="Områder (omfang)" value={(a.area_reviews || []).map((r) => r.area_name).join(", ")} />
        <Row label="Planlagte punkter/krav" value={a.planned_points} />
        <Row label="Auditerede punkter / agenda" value={a.audited_points} />
        <div className="sm:col-span-2"><span className="text-stone-500 text-sm">Auditerede lovkrav og andre krav</span>
          <div className="flex flex-wrap gap-2 mt-1">{(a.audited_requirements || []).length ? a.audited_requirements.map((r, i) => <span key={i} className="text-xs px-2 py-1 rounded-lg bg-[#F3F1EC] text-stone-700 border border-[#EBE8E3]">{r}</span>) : <span className="text-stone-400 text-sm">—</span>}</div>
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="text-base font-semibold text-[#1A1919] mb-3">Gennemgåede områder</h2>
        <div className="space-y-2">
          {(a.area_reviews || []).map((r) => (
            <div key={r.area_id} className="flex items-start gap-2 text-sm" data-testid={`report-review-${r.area_id}`}>
              {r.status === "reviewed" ? <CheckCircle2 className="h-4 w-4 text-[#5f6b4a] mt-0.5 shrink-0" /> : <MinusCircle className="h-4 w-4 text-[#A83232] mt-0.5 shrink-0" />}
              <div><span className="font-medium text-[#1A1919]">{r.area_name}</span>
                {r.status === "reviewed" && r.note && <p className="text-stone-600 whitespace-pre-wrap">{r.note}</p>}
                {r.status === "not_reviewed" && <p className="text-[#A83232]">Ikke gennemgået — {r.reason || "ingen begrundelse"}</p>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {(a.prior_followups || []).filter((p) => p.finding).length > 0 && (
        <Card className="p-5">
          <h2 className="text-base font-semibold text-[#1A1919] mb-3 flex items-center gap-2"><Link2 className="h-4 w-4 text-[#806B61]" /> Opfølgning fra tidligere audit</h2>
          <div className="space-y-2">
            {(a.prior_followups || []).filter((p) => p.finding).map((p) => (
              <div key={p.finding_id} className="text-sm rounded-xl border border-[#EBE8E3] p-3">
                <button onClick={() => nav(`/kvalitet/audit/fund/${p.finding.id}`)} className="text-xs font-mono text-[#806B61] hover:underline">{p.finding.finding_no}</button>
                <span className="text-stone-500 text-xs ml-2">{p.finding.area_name}</span>
                <p className="text-stone-700 mt-1">{p.finding.description}</p>
                {p.note && <p className="text-stone-600 mt-1 italic">Notat: {p.note}</p>}
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-[#1A1919]">Fund</h2>
          <div className="flex gap-2 text-xs">
            <span className="px-2 py-0.5 rounded-full bg-[#F1DEDA] text-[#A83232]">{a.counts?.Afvigelse || 0} afv.</span>
            <span className="px-2 py-0.5 rounded-full bg-[#F4EFEC] text-[#806B61]">{a.counts?.Observation || 0} obs.</span>
            <span className="px-2 py-0.5 rounded-full bg-[#E7EADF] text-[#5f6b4a]">{a.counts?.Forslag || 0} forslag</span>
          </div>
        </div>
        {(a.findings || []).length === 0 ? <p className="text-sm text-stone-400">Ingen fund.</p> : (
          <div className="space-y-3">
            {a.findings.map((f) => {
              const c = cards[f.id];
              return (
              <div key={f.id} data-testid={`report-finding-${f.id}`}>
                {c ? <FindingCard finding={c} onChanged={loadCards} /> : (
                  <div className="rounded-xl border border-[#EBE8E3] p-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => nav(`/kvalitet/audit/fund/${f.id}`)} className="text-xs font-mono text-[#806B61] hover:underline">{f.finding_no}</button>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${CAT_BADGE[f.category]}`}>{f.category}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${ST_BADGE[f.status]}`}>{f.status}</span>
                    </div>
                    <p className="text-sm text-stone-700 mt-2 whitespace-pre-wrap">{f.description}</p>
                  </div>
                )}
                {((f.images || []).length > 0 || (f.followup_images || []).length > 0) && <div className="flex flex-wrap gap-2 mt-2 px-1">{[...(f.images || []), ...(f.followup_images || [])].map((img) => (
                  <a key={img.id} href={`${BACKEND}/api/audit/findings/${f.id}/images/${img.id}`} target="_blank" rel="noreferrer"><img src={`${BACKEND}/api/audit/findings/${f.id}/images/${img.id}?thumb=1`} alt="" className="h-16 w-16 object-cover rounded-lg border border-[#EBE8E3]" /></a>
                ))}</div>}
                {(f.refs?.iso9001 || f.refs?.iso14001 || f.refs?.other) && <p className="text-xs text-stone-500 mt-1 px-1">Krav: {[f.refs.iso9001, f.refs.iso14001, f.refs.other].filter(Boolean).join(" · ")}</p>}
              </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <h2 className="text-base font-semibold text-[#1A1919] mb-2">Samlet konklusion</h2>
        <p className="text-sm text-stone-700 whitespace-pre-wrap">{a.conclusion || "—"}</p>
      </Card>

      {shareOpen && <ShareDialog id={id} token={token} setToken={setToken} a={a} setA={setA} onClose={() => setShareOpen(false)} />}
    </div>
  );
}
